/**
 * Reactive engine (ADR-0043 F1 · slice 3).
 *
 * The framework-agnostic core that makes a page "live": it holds the scope
 * state and the cells, and on every state write recomputes ONLY the cells the
 * dependency graph says are affected, in dependency order. No React, no
 * network — those are the next slices (a thin React hook + the query refetch
 * wiring sit ON TOP of this). Keeping the engine pure is what lets us unit-test
 * the reactivity (and bench it) without a DOM.
 *
 * Two kinds of cell:
 *  - **derived** — has a `{{ }}` `source`; the engine evaluates it via the
 *    existing sandboxed `pageExpr.evalExpr` and writes the result to `produces`.
 *  - **external** — an input / a query result: its value comes from OUTSIDE
 *    (`set(...)` for an input, or a fetch result fed back in). The engine never
 *    computes it, but it's a node in the graph (so writes propagate through it);
 *    when a write reaches an external cell, the engine reports it **stale** so
 *    the integration layer can refetch it and feed the result back.
 *
 * Cycles never hang the engine: a write triggers a SINGLE ordered pass over the
 * affected set (cyclic cells are evaluated once, not iterated to a fixpoint).
 */

import { renderTemplate } from "../pageExpr";
import { extractTemplateDeps, type DepPath } from "./deps";
import { buildGraph, type ReactiveCell } from "./graph";

/** A page cell as declared to the engine. */
export interface EngineCell {
  id: string;
  /** Derived cell: the `{{ }}` template source (its deps are extracted from it). */
  source?: string;
  /** External cell (input / query): the paths it reads, declared explicitly. */
  deps?: DepPath[];
  /** State path this cell writes when it (re)computes or is set. */
  produces?: DepPath;
  /** True for inputs/queries the engine must NOT compute itself. */
  external?: boolean;
}

/** What a recompute touched, returned to the caller (and to subscribers). */
export interface RecomputeResult {
  /** Cell ids that were re-evaluated this pass, in order. */
  recomputed: string[];
  /** External cell ids (inputs/queries) the change invalidated — refetch these. */
  stale: string[];
  /** The concrete state writes the derived recomputes produced. */
  changes: { path: DepPath; value: unknown }[];
}

export interface ReactiveEngine {
  /** Evaluate every derived cell once, in order (initial page render). */
  evaluateAll(): RecomputeResult;
  /** Write external state at `path`, then recompute everything it affects. */
  set(path: DepPath, value: unknown): RecomputeResult;
  /** Read the current value at a state path. */
  get(path: DepPath): unknown;
  /** The whole scope (read-only view) — what bindings evaluate against. */
  readonly scope: Record<string, unknown>;
  /** Cell groups the graph found to be cyclic (reported, never auto-iterated). */
  readonly cycles: string[][];
  /** Subscribe to every recompute; returns an unsubscribe fn. */
  subscribe(fn: (r: RecomputeResult) => void): () => void;
}

/** Read a nested path off `obj`, null-safe (mirrors pageExpr's lenient reads). */
function getPath(obj: Record<string, unknown>, path: DepPath): unknown {
  let cur: unknown = obj;
  for (const seg of path) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[seg];
  }
  return cur;
}

/** Write a nested path on `obj`, creating intermediate objects as needed. */
function setPath(obj: Record<string, unknown>, path: DepPath, value: unknown): void {
  if (path.length === 0) return;
  let cur = obj;
  for (let i = 0; i < path.length - 1; i++) {
    const seg = path[i];
    const next = cur[seg];
    if (next == null || typeof next !== "object") {
      cur[seg] = {};
    }
    cur = cur[seg] as Record<string, unknown>;
  }
  cur[path[path.length - 1]] = value;
}

export function createReactiveEngine(
  cells: EngineCell[],
  initialState: Record<string, unknown> = {},
): ReactiveEngine {
  const byId = new Map(cells.map((c) => [c.id, c]));
  const state: Record<string, unknown> = structuredCloneSafe(initialState);
  const subscribers = new Set<(r: RecomputeResult) => void>();

  // Build the graph: derived cells get deps from their source; external cells
  // declare deps explicitly.
  const graphCells: ReactiveCell[] = cells.map((c) => ({
    id: c.id,
    deps: c.external ? (c.deps ?? []) : c.source ? extractTemplateDeps(c.source) : [],
    produces: c.produces,
  }));
  const graph = buildGraph(graphCells);

  /** Evaluate one derived cell against the live scope and write its output. */
  function computeCell(c: EngineCell, changes: { path: DepPath; value: unknown }[]): void {
    if (c.external || !c.source) return;
    let value: unknown;
    try {
      value = renderTemplate(c.source, state);
    } catch {
      value = undefined; // a bad binding degrades to undefined, never throws
    }
    if (c.produces) {
      setPath(state, c.produces, value);
      changes.push({ path: c.produces, value });
    }
  }

  function emit(r: RecomputeResult): void {
    for (const fn of subscribers) fn(r);
  }

  function evaluateAll(): RecomputeResult {
    const changes: { path: DepPath; value: unknown }[] = [];
    const recomputed: string[] = [];
    for (const id of graph.order) {
      const c = byId.get(id);
      if (c && !c.external && c.source) {
        computeCell(c, changes);
        recomputed.push(id);
      }
    }
    const r: RecomputeResult = { recomputed, stale: [], changes };
    emit(r);
    return r;
  }

  function set(path: DepPath, value: unknown): RecomputeResult {
    setPath(state, path, value);
    const affected = graph.affected(path);
    const changes: { path: DepPath; value: unknown }[] = [];
    const recomputed: string[] = [];
    const stale: string[] = [];
    for (const id of affected) {
      const c = byId.get(id);
      if (!c) continue;
      if (c.external) {
        stale.push(id);
      } else if (c.source) {
        computeCell(c, changes);
        recomputed.push(id);
      }
    }
    const r: RecomputeResult = { recomputed, stale, changes };
    emit(r);
    return r;
  }

  return {
    evaluateAll,
    set,
    get: (path) => getPath(state, path),
    scope: state,
    cycles: graph.cycles,
    subscribe(fn) {
      subscribers.add(fn);
      return () => subscribers.delete(fn);
    },
  };
}

/** `structuredClone` with a plain-object fallback (jsdom/older runtimes). */
function structuredCloneSafe<T>(v: T): T {
  try {
    return structuredClone(v);
  } catch {
    return JSON.parse(JSON.stringify(v ?? {})) as T;
  }
}
